import type React from 'react';
import { useState } from 'react';
import { QueryPage } from '@/components/QueryPage';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useGenerateRorosCanvas, useRorosCanvas, useSaveRorosCanvas } from '@/hooks/queries';
import { useAiProviderConfigured } from '@/hooks/roros/overarching';
import { t } from '@/lib/i18n';
import type { RorosCanvasBlockKey, RorosCanvasBlocksDTO, RorosCanvasType } from '@/types/roros/canvases';
import { Printer, RefreshCw, RotateCcw } from 'lucide-react';
import { CanvasPrintStyles } from './CanvasPrintStyles';

interface CanvasShellProps {
  type: RorosCanvasType;
  title: string;
  description: string;
  testId: string;
  children: (args: {
    blocks: RorosCanvasBlocksDTO;
    canEdit: boolean;
    isSaving: boolean;
    isGenerating: boolean;
    aiAvailable: boolean;
    saveBlocks: (blocks: RorosCanvasBlocksDTO) => void;
    regenerateBlock: (blockKey: RorosCanvasBlockKey) => void;
  }) => React.ReactNode;
  canEdit: boolean;
}

export function CanvasShell({ type, title, description, testId, children, canEdit }: CanvasShellProps) {
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const query = useRorosCanvas(type);
  const aiProviderQuery = useAiProviderConfigured();
  const saveMutation = useSaveRorosCanvas();
  const generateMutation = useGenerateRorosCanvas();

  function saveBlocks(blocks: RorosCanvasBlocksDTO) {
    saveMutation.mutate({ type, blocks });
  }

  function generateFillEmpty() {
    generateMutation.mutate({ type, body: { mode: 'fill_empty' } });
  }

  function resetOverwrite() {
    generateMutation.mutate({ type, body: { mode: 'overwrite' } });
  }

  function regenerateBlock(blockKey: RorosCanvasBlockKey) {
    generateMutation.mutate({ type, body: { mode: 'block', blockKey } });
  }

  function printCanvas() {
    window.print();
  }

  const isBusy = saveMutation.isPending || generateMutation.isPending;

  return (
    <div className="space-y-4" data-testid={testId}>
      <CanvasPrintStyles />
      <ConfirmDialog
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={resetOverwrite}
        title={t('Reset & overwrite from data')}
        description={t('This discards all canvas edits and regenerates every block from current company data. Continue?')}
        confirmText={t('Reset and overwrite')}
        cancelText={t('Cancel')}
        variant="destructive"
        isLoading={generateMutation.isPending}
        confirmTestId={`canvas-reset-confirm-${type}`}
        cancelTestId={`canvas-reset-cancel-${type}`}
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between roros-canvas-no-print">
        <div>
          <h2 className="text-xl font-semibold text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && (
            <>
              <Button
                variant="secondary"
                onClick={generateFillEmpty}
                isLoading={generateMutation.isPending}
                disabled={isBusy}
                data-testid={`canvas-generate-${type}`}
              >
                <RefreshCw className="mr-1.5 h-4 w-4" />
                {generateMutation.isPending ? t('Generating...') : t('Generate / re-sync from data')}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowResetConfirm(true)}
                disabled={isBusy}
                data-testid={`canvas-reset-${type}`}
              >
                <RotateCcw className="mr-1.5 h-4 w-4" />
                {t('Reset & overwrite')}
              </Button>
            </>
          )}
          <Button variant="outline" onClick={printCanvas} data-testid={`canvas-print-${type}`}>
            <Printer className="mr-1.5 h-4 w-4" />
            {t('Print')}
          </Button>
        </div>
      </div>

      {saveMutation.isError && (
        <Alert variant="error" title={t('Could not save canvas')} className="roros-canvas-no-print">
          {saveMutation.error?.message ?? t('Please try again.')}
        </Alert>
      )}
      {generateMutation.isError && (
        <Alert variant="error" title={t('Could not generate canvas')} className="roros-canvas-no-print">
          {generateMutation.error?.message ?? t('Please try again.')}
        </Alert>
      )}

      <QueryPage query={query} loadingText={t('Loading canvas...')} emptyMessage={t('No canvas data available.')}> 
        {(data) => (
          <div className="roros-canvas-print-area rounded-xl bg-background p-3" data-testid={`canvas-print-area-${type}`}>
            {children({
              blocks: data.canvas.blocks,
              canEdit,
              isSaving: isBusy,
              isGenerating: generateMutation.isPending,
              aiAvailable: data.aiAvailable ?? aiProviderQuery.data ?? true,
              saveBlocks,
              regenerateBlock,
            })}
          </div>
        )}
      </QueryPage>
    </div>
  );
}
